import { ColDef, DropIndicatorPosition, IRowNode } from 'ag-grid-community';
import { IFileDropIndicator, IFolderGridFile } from '../types/folder-grid.type';
import { computed, signal } from '@angular/core';
import { SaveFolderInput } from '@core/types';
import { getAllUserFolders } from '@unified-ui/user-folders';

//More info about this https://www.ag-grid.com/angular-data-grid/tree-data-row-dragging/
export function getFileDropIndicator(files: IFolderGridFile[], source: IFolderGridFile | null | undefined, target: IFolderGridFile | null | undefined, reorderOnly: boolean): IFileDropIndicator | null {
    if (!source || target === source) {
        return null;
    }

    let file = target ?? source;
    let index = 0;
    let parentId: string | undefined;
    let dropIndicatorPosition: DropIndicatorPosition = 'none';

    if (!target) {
        // Append in the root
        index = files.length;
        dropIndicatorPosition = 'none';
    } else if (target.type === 'folder') {
        parentId = target.id;
        const children = files.filter(f => f.parentId === target.id);
        if (children.length > 0) {
            file = children[0]; // above first child
            dropIndicatorPosition = 'above';
            index = 0;
        } else {
            dropIndicatorPosition = 'inside'; // No children, drop inside the folder
            index = 0;
        }
    } else {
        parentId = getNewParentId(source, target, reorderOnly);
        const filtered = files.filter(f => f.id !== source.id);
        const siblings = filtered.filter(f => f.parentId === parentId);
        index = siblings.findIndex(f => f.id === target.id);
        if (index === -1) {
            index = siblings.length;
        } else {
            index = index + 1; // always insert after target
        }
        dropIndicatorPosition = 'below';
    }

    return { parentId, index, file, dropIndicatorPosition };
}

/**
 * Moves a file or folder in a flat tree structure using parentId.
 * - Prevents moving a folder into itself or its descendants.
 * - Handles reordering among siblings and moving to a new parent.
 * - Returns a new array, does not mutate the input.
 */
export function moveFiles(files: IFolderGridFile[], source: IFolderGridFile | null | undefined, target: IFolderGridFile | null | undefined, reorderOnly: boolean): IFolderGridFile[] {
    if (source === target || !source) {
        return files;
    }
    if (target && isDescendant(source, target, files)) {
        return files; // Prevent moving a folder into itself or its descendants
    }
    const ctx = getFileDropIndicator(files, source, target, reorderOnly);
    if (!ctx) {
        // Insert at root (parentId undefined), at the end
        const filtered = files.filter(f => f.id !== source.id);
        const newFile: IFolderGridFile = { ...source, parentId: undefined };
        const result: IFolderGridFile[] = [];
        for (const file of filtered) {
            result.push(file);
        }
        // Always insert at the end of root
        result.push(newFile);
        return result;
    }
    const filtered = files.filter(f => f.id !== source.id);
    const newFile: IFolderGridFile = { ...source, parentId: ctx.parentId };
    const result: IFolderGridFile[] = [];
    let siblingIdx = 0;
    let inserted = false;
    for (const file of filtered) {
        if (file.parentId === ctx.parentId) {
            if (siblingIdx === ctx.index && !inserted) {
                result.push(newFile);
                inserted = true;
            }
            siblingIdx++;
        }
        result.push(file);
    }
    if (!inserted) {
        result.push(newFile);
    }
    return result;
}

/**
 * Returns true if target is a descendant of source (or the same node).
 * Used to prevent invalid moves.
 */
function isDescendant(source: IFolderGridFile, target: IFolderGridFile, files: IFolderGridFile[]): boolean {
    if (source.id === target.id) return true;
    let parent = target.parentId;
    while (parent) {
        if (parent === source.id) return true;
        parent = files.find(f => f.id === parent)?.parentId;
    }
    return false;
}

/**
 * Returns the new parentId for a move operation.
 */
function getNewParentId(source: IFolderGridFile, target: IFolderGridFile | null | undefined, reorderOnly: boolean): string | undefined {
    if (reorderOnly) {
        return source.parentId;
    }
    if (!target) {
        return undefined;
    }
    if (target.type === 'folder') {
        return target.id;
    } else {
        return target.parentId;
    }
}

/**
 * Utility function to check if a folder has children
 */
export const hasFolderChildren = (node: IRowNode<IFolderGridFile>): boolean => {
    return !!node?.allChildrenCount || !!node.data?.content?.length;
};

/**
 * @description Determines if the given folder contains any content.
 * @param {IFolderGridFile | null} folder - The folder to check for content. Can be `null`.
 * @returns {boolean} `true` if the folder has content, otherwise `false`.
 */
export const hasFolderContent = (folder: IFolderGridFile | null | undefined): boolean => {
    const content = folder?.content;
    if (Array.isArray(content)) {
        return content.length > 0;
    }
    if (typeof content === 'object') {
        return Object.values(content).some((value: unknown) => {
            if (Array.isArray(value)) return value.length > 0;
            if (typeof value === 'object' && value !== null) return Object.keys(value).length > 0;
            return !!value;
        });
    }
    return !!content;
};

export const hasFolderChildrenOrContent = (node: IRowNode<IFolderGridFile>): boolean => {
    return hasFolderChildren(node) || hasFolderContent(node.data);
}
